/**
 * Initializes and configures a WebRTC player to stream video from a Wowza server.
 * 
 * @param {string} wowzaApplicationName - The name of the Wowza WebRTC application.
 * @param {string} cameraName - The identifier for the camera.
 * @param {string} selectedSiteName - The name of the site where the camera is located.
 * @param {string} wowzaStreamlock - The secure WebSocket endpoint.
 * @param {string} videoID - The ID of the HTML video element where the stream will be displayed.
 * @param {string} alias - The alias or name of the stream.
 * @param {Date} startDate - The timestamp indicating when the streaming started.
 * @param {boolean} isCameraPTZ - Indicates whether the camera supports PTZ (pan-tilt-zoom).
 * @param {object} logger - Logger object for handling debug and trace logs.
 * @param {object} callback - Object containing methods for handling WebRTC events and status updates.
 * @param {string} currentRTSPUrl - The RTSP URL of the camera stream.
 */
function UnrealWebRTCPlayer(wowzaApplicationName, cameraName, selectedSiteName, wowzaStreamlock, videoID, alias, startDate, isCameraPTZ, logger, callback, currentRTSPUrl) {
    /* Get these from WowzaStreamingEngine WebRTC Application */
    let applicationName = wowzaApplicationName;
    let streamName = alias;
    const wssUrl = `wss://${wowzaStreamlock}/webrtc-session.json`;
    let remoteStream = videoID;
    let wsConnection, videoElement = null;
    let retry = 0;
    const maxRetry = 120;
    let peerConnection = null;

    const wsConnect = () => {
        let _this = this;
        let streamInfo = { applicationName, streamName };
        let userData = {};

        try {
            wsConnection = new WebSocket(wssUrl);
        } catch (e) {
            logger.streamDebugLog(selectedSiteName, cameraName, streamName, "Websocket Error", currentRTSPUrl, e);
        }
        wsConnection.binaryType = "arraybuffer";

        wsConnection.onopen = () => {
            peerConnection = new RTCPeerConnection();

            peerConnection.onicecandidate = _this.gotIceCandidate;
            peerConnection.onconnectionstatechange = onConnStateChange;

            peerConnection.ontrack = (event) => {
                try {
                    videoElement = document.getElementById(remoteStream);
                    videoElement.muted = true;
                    videoElement.srcObject = event.streams[0];
                    videoElement.addEventListener('canplaythrough', () => {
                        videoElement.play();
                    });
                    logger.streamingTrace("webRTC_Stream", cameraName, selectedSiteName, "Loaded", streamName.replace('.stream', ''), "Stream Loaded in the Video Player", isCameraPTZ, currentRTSPUrl, startDate);
                } catch (error) {
                    logger.streamDebugLog(selectedSiteName, cameraName, streamName, "Peer Conneciton Error", currentRTSPUrl, error);
                    videoElement.src = window.URL.createObjectURL(event.streams[0]);
                }
            };
            sendPlayGetOffer();
        };

        wsConnection.onerror = (error) => {
            callback.handleWebSocketConnectionError();
            logger.streamingTrace("webRTC_Stream", cameraName, selectedSiteName, "Error", streamName.replace('.stream', ''), "Websocket Error", isCameraPTZ, currentRTSPUrl, error);
        }

        function onConnStateChange(event) {
            callback.handlePeerConnectionStatus(event.currentTarget.connectionState, streamName);
        }

        const sendPlayGetOffer = () => {
            if (wsConnection && wsConnection.readyState == WebSocket.OPEN) {
                wsConnection.send(
                    '{"direction":"play", "command":"getOffer", "streamInfo":' +
                    JSON.stringify(streamInfo) +
                    ', "userData":' +
                    JSON.stringify(userData) +
                    "}"
                );
            }
        };

        this.stop = function stop() {
            if (peerConnection != null) {
                peerConnection.onconnectionstatechange = null;
                peerConnection.close();
            }
            peerConnection = null;
            
            if (wsConnection != null) {
                wsConnection.onerror = null;
                wsConnection.close(1000, "Normal closure");
            }
            wsConnection = null;

            videoElement = document.getElementById(remoteStream);
            if (videoElement) {
                videoElement.src = "";
                videoElement.srcObject = null;
            }
        }

        this.SendPlayGetOffer = function SendPlayGetOffer() {
            sendPlayGetOffer();
        }

        wsConnection.onmessage = function (evt) {
            let msgJSON = JSON.parse(evt.data);
            const msgStatus = Number(msgJSON.status);
            const msgCommand = msgJSON.command;

            callback.handleWebSocketStatus(msgStatus, msgJSON.statusDescription, streamName);

            if (msgStatus != 200) {
                retry++;
                if (retry < maxRetry) {
                    setTimeout(sendPlayGetOffer, 500);
                }
            } else {
                retry = 0;
                let streamInfoResponse = msgJSON.streamInfo;
                if (streamInfoResponse !== undefined) {
                    streamInfo.sessionId = streamInfoResponse.sessionId;
                }

                let sdpData = msgJSON.sdp;
                if (sdpData != null) {

                    if (mungeSDP != null) {
                        msgJSON.sdp.sdp = mungeSDP(msgJSON.sdp.sdp);
                    }

                    // Enhance here if Safari is a published stream.
                    peerConnection
                        .setRemoteDescription(new RTCSessionDescription(msgJSON.sdp))
                        .then(() => peerConnection
                            .createAnswer()
                            .then((description) => {
                                peerConnection
                                    .setLocalDescription(description)
                                    .then(() => {
                                        wsConnection.send(
                                            '{"direction":"play", "command":"sendResponse", "streamInfo":' +
                                            JSON.stringify(streamInfo) +
                                            ', "sdp":' +
                                            JSON.stringify(
                                                description
                                            ) +
                                            ', "userData":' +
                                            JSON.stringify(userData) +
                                            "}"
                                        );
                                    })
                                    .catch((err) => {});
                            })
                        )
                        .catch((err) => {});
                }

                let iceCandidates = msgJSON.iceCandidates;
                if (iceCandidates != null) {
                    for (let index in iceCandidates) {
                        peerConnection.addIceCandidate(new RTCIceCandidate(iceCandidates[index]));
                    }
                }
            }

            if ("sendResponse".localeCompare(msgCommand) == 0) {
                if (wsConnection != null) {
                    wsConnection.close(1000, "Normal closure");
                }
                wsConnection = null;
            }
        };

        wsConnection.onclose = (evt) => {};
    };


    const mungeSDP = (sdpStr) => {
        // For greatest playback compatibility,
        // force H.264 playback to baseline (42e01f).
        let sdpLines = sdpStr.split(/\r\n/);
        let sdpStrRet = "";

        for (let sdpIndex in sdpLines) {
            let sdpLine = sdpLines[sdpIndex];

            if (sdpLine.length == 0) continue;

            if (sdpLine.includes("profile-level-id")) {
                // The profile-level-id string has three parts: XXYYZZ, where
                //   XX: 42 baseline, 4D main, 64 high
                //   YY: constraint
                //   ZZ: level ID
                // Look for codecs higher than baseline and force downward.
                let profileLevelId = sdpLine.substr(
                    sdpLine.indexOf("profile-level-id") + 17,
                    6
                );
                let profile = Number("0x" + profileLevelId.substr(0, 2));
                let constraint = Number("0x" + profileLevelId.substr(2, 2));
                let level = Number("0x" + profileLevelId.substr(4, 2));
                if (profile > 0x42) {
                    profile = 0x42;
                    constraint = 0xe0;
                    level = 0x1f;
                }
                let newProfileLevelId =
                    ("00" + profile.toString(16)).slice(-2).toLowerCase() +
                    ("00" + constraint.toString(16)).slice(-2).toLowerCase() +
                    ("00" + level.toString(16)).slice(-2).toLowerCase();

                sdpLine = sdpLine.replace(profileLevelId, newProfileLevelId);
            }

            sdpStrRet += sdpLine;
            sdpStrRet += "\r\n";
        }

        return sdpStrRet;
    };

    /* initialize and play, wire in play button here */

    if (applicationName == "" || streamName == "" || wssUrl == "") {
        alert("Please fill out the connection details");
    } else {
        wsConnect();
    }
}
